import { defineStore } from 'pinia';
import http from '@/composables/api/http';

export const SESSION_STATUS = {
  UNKNOWN: 'unknown',
  VALID: 'valid',
  INVALID: 'invalid',
  CHECKING: 'checking',
};

let resolveSessionReady = null;

export const useSessionStatusStore = defineStore('sessionStatus', {
  state: () => ({
    sessionStatus: SESSION_STATUS.UNKNOWN,
    sessionReadyPromise: new Promise((resolve) => {
      resolveSessionReady = resolve;
    }),
  }),

  getters: {
    isSessionValid: (state) => state.sessionStatus === SESSION_STATUS.VALID,
    isSessionInvalid: (state) => state.sessionStatus === SESSION_STATUS.INVALID,
    isChecking: (state) => state.sessionStatus === SESSION_STATUS.CHECKING,
    hasSessionStatus: (state) => state.sessionStatus !== SESSION_STATUS.UNKNOWN,
  },

  actions: {
    /**
     * Check session validity by calling /session/status endpoint
     */
    async checkSession() {
      this.sessionStatus = SESSION_STATUS.CHECKING;

      try {
        const response = await http.get('/session/status', { withCredentials: true });
        const { valid, type } = response?.data || {};

        if (valid && (type === 'authenticated' || type === 'verified')) {
          this.sessionStatus = SESSION_STATUS.VALID;
        } else {
          this.sessionStatus = SESSION_STATUS.INVALID;
        }
      } catch (err) {
        // For any errors (401/419 or network issues), we can't assume session is valid
        // Keep as INVALID to force re-authentication
        // eslint-disable-next-line no-console
        console.error('Session check failed:', err?.response?.status || err?.message || err);
        this.sessionStatus = SESSION_STATUS.INVALID;
      } finally {
        this.resolveSessionReady();
      }
    },

    /**
     * Reset session status to CHECKING so the template shows the blank loading
     * state immediately — prevents flashing order content or the login-link UI
     * while a new session check is in flight.
     */
    resetSessionStatus() {
      this.sessionStatus = SESSION_STATUS.CHECKING;
    },

    /**
     * Mark session as valid (e.g., when successfully authenticated)
     */
    setSessionValid() {
      this.sessionStatus = SESSION_STATUS.VALID;
      this.resolveSessionReady();
    },

    /**
     * Create a fresh promise for status-page tracking to await.
     * Call this in the route guard before any code that triggers tracking.
     */
    resetSessionReady() {
      this.sessionReadyPromise = new Promise((resolve) => {
        resolveSessionReady = resolve;
      });
    },

    /**
     * Resolve the session-ready promise, unblocking any awaiting tracking calls.
     */
    resolveSessionReady() {
      if (resolveSessionReady) {
        resolveSessionReady();
        resolveSessionReady = null;
      }
    },
  },
});
